import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TourContext = createContext(null);

// Tour steps definitions per page
const TOUR_STEPS = {
  dashboard: [
    { target: '[data-testid="nav-dashboard"]', content: 'This is your main dashboard showing key metrics at a glance — therapist count, final visits, and projections.', title: 'Dashboard' },
    { target: '[data-testid="nav-reports"]', content: 'The Reports page lets you generate, export, and email weekly reports for therapists and agencies.', title: 'Reports' },
    { target: '[data-testid="nav-personnel"]', content: 'Manage all personnel records — therapists, office staff, documents, and PTO.', title: 'Personnel' },
    { target: '[data-testid="notifications-button"]', content: 'Notifications show expiring credentials, pending reviews, and PTO requests.', title: 'Notifications' },
    { target: '[data-testid="help-tour-button"]', content: 'Click this Help button anytime to get a guided tour of the current page!', title: 'Help Button' },
  ],
  reports: [
    { target: '[data-testid="export-weekly-pdf-button"]', content: 'Generate a PDF report showing each therapist\'s weekly visits for the last 6 weeks plus projections.', title: 'Therapist Weekly Report' },
    { target: '[data-testid="export-weekly-agency-button"]', content: 'Generate an agency-level weekly summary showing all agencies and their visit totals.', title: 'Agency Weekly Report' },
    { target: '[data-testid="export-eval-discharge-button"]', content: 'See Evaluations vs Discharges broken down by agency for the current projection week.', title: 'Eval vs DC Report' },
    { target: '[data-testid="email-reports-button"]', content: 'Email selected reports as PDF attachments to your team with the weekly metrics in the body.', title: 'Email Reports' },
    { target: '[data-testid="date-range-report-button"]', content: 'Generate reports for custom time periods — weekly, monthly, quarterly, annual, or a custom date range.', title: 'Reports by Period' },
    { target: '[data-testid="profile-filter"]', content: 'Filter the visit data by therapist, agency, task type, status, or date range.', title: 'Filters' },
  ],
  personnel: [
    { target: '[data-testid="add-personnel-btn"]', content: 'Add a new personnel record. The system automatically checks for duplicates before creating.', title: 'Add Personnel' },
    { target: '[data-testid="global-search"]', content: 'Use the global search to quickly find any therapist, agency, or personnel by name.', title: 'Search' },
  ],
  applicants: [
    { target: '[data-testid="add-applicant-btn"]', content: 'Add a new applicant to the hiring pipeline. Checks for duplicates against existing applicants and personnel.', title: 'Add Applicant' },
  ],
  applicantDetail: [
    { target: '[data-testid="convert-to-personnel-btn"]', content: 'One-click convert this applicant to a personnel record when hired. Pre-fills all their info.', title: 'Convert to Personnel' },
    { target: '[data-testid="hire-btn"]', content: 'Send an offer letter to the applicant via email using a template.', title: 'Hire / Send Offer' },
    { target: '[data-testid="tab-interviews"]', content: 'Schedule and manage interviews — track rounds, dates, interviewers, and completion status.', title: 'Interviews Tab' },
    { target: '[data-testid="tab-scorecards"]', content: 'Rate applicants on Communication, Experience, Technical Skills, Culture Fit, and Professionalism.', title: 'Scorecards Tab' },
  ],
  upload: [
    { target: '[data-testid="drop-zone"]', content: 'Drag and drop your Excel file here, or click to browse. Supports .xlsx and .xls files.', title: 'Upload File' },
  ],
  admin: [
    { target: '[data-testid="nav-admin"]', content: 'The Admin Console gives you access to user management, permissions, tracking config, notifications, analytics, and more.', title: 'Admin Console' },
  ],
};

// Page path to tour key mapping
const PATH_TO_TOUR = {
  '/dashboard': 'dashboard',
  '/reports': 'reports',
  '/personnel': 'personnel',
  '/ats/applicants': 'applicants',
  '/upload': 'upload',
  '/admin': 'admin',
};

// Tour overlay component - custom-built for reliability
function TourOverlay({ steps, stepIndex, onNext, onBack, onSkip, onClose }) {
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowSide, setArrowSide] = useState('top');
  const tooltipRef = useRef(null);

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  // Position calculation
  useEffect(() => {
    if (!step) return;
    let cancelled = false;

    const positionTooltip = () => {
      const targetEl = document.querySelector(step.target);
      if (!targetEl) {
        // Target not found — show centered tooltip with note
        setTargetRect(null);
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        });
        return;
      }

      // Scroll target into view (centered) before measuring
      try {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      } catch (e) {
        targetEl.scrollIntoView();
      }

      // Wait for scroll to settle, then measure
      setTimeout(() => {
        if (cancelled) return;
        const rect = targetEl.getBoundingClientRect();
        setTargetRect(rect);

        const tooltipWidth = 360;
        const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 200;
        const margin = 12;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Choose placement: prefer below, then above, then right, then left
        let top, left, side;
        const spaceBelow = vh - rect.bottom;
        const spaceAbove = rect.top;
        const spaceRight = vw - rect.right;
        const spaceLeft = rect.left;

        if (spaceBelow >= tooltipHeight + margin) {
          top = rect.bottom + margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          side = 'top';
        } else if (spaceAbove >= tooltipHeight + margin) {
          top = rect.top - tooltipHeight - margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          side = 'bottom';
        } else if (spaceRight >= tooltipWidth + margin) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + margin;
          side = 'left';
        } else if (spaceLeft >= tooltipWidth + margin) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - margin;
          side = 'right';
        } else {
          // Fallback: center
          top = vh / 2 - tooltipHeight / 2;
          left = vw / 2 - tooltipWidth / 2;
          side = 'top';
        }

        // Clamp horizontally
        left = Math.max(margin, Math.min(left, vw - tooltipWidth - margin));
        // Clamp vertically
        top = Math.max(margin, Math.min(top, vh - tooltipHeight - margin));

        setTooltipStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          width: `${tooltipWidth}px`,
        });
        setArrowSide(side);
      }, 350);
    };

    positionTooltip();

    const handleResize = () => positionTooltip();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [step, stepIndex]);

  if (!step) return null;

  // Build SVG mask cutout for spotlight
  const padding = 8;
  const radius = 8;
  const cutout = targetRect ? {
    x: Math.max(0, targetRect.left - padding),
    y: Math.max(0, targetRect.top - padding),
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  } : null;

  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: 10000, pointerEvents: 'none' }}
      data-testid="tour-overlay"
    >
      {/* SVG overlay with cutout */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'fixed', top: 0, left: 0, pointerEvents: 'auto' }}
        onClick={onSkip}
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {cutout && (
              <rect
                x={cutout.x}
                y={cutout.y}
                width={cutout.width}
                height={cutout.height}
                rx={radius}
                ry={radius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.55)"
          mask="url(#tour-mask)"
        />
        {cutout && (
          <rect
            x={cutout.x}
            y={cutout.y}
            width={cutout.width}
            height={cutout.height}
            rx={radius}
            ry={radius}
            fill="none"
            stroke="#1a3a5c"
            strokeWidth="3"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="bg-white rounded-xl shadow-2xl"
        style={{ ...tooltipStyle, pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="tour-tooltip"
      >
        {/* Arrow */}
        {targetRect && (
          <div
            className="absolute w-3 h-3 bg-white rotate-45"
            style={{
              ...(arrowSide === 'top' && { top: '-6px', left: '50%', marginLeft: '-6px' }),
              ...(arrowSide === 'bottom' && { bottom: '-6px', left: '50%', marginLeft: '-6px' }),
              ...(arrowSide === 'left' && { left: '-6px', top: '50%', marginTop: '-6px' }),
              ...(arrowSide === 'right' && { right: '-6px', top: '50%', marginTop: '-6px' }),
            }}
          />
        )}

        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                <HelpCircle size={16} className="text-primary" />
              </div>
              <h3 className="font-bold text-base text-foreground" data-testid="tour-step-title">
                {step.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              data-testid="tour-close-btn"
              aria-label="Close tour"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed mb-4" data-testid="tour-step-content">
            {step.content}
          </p>

          {step.video && (
            <div className="mb-4 rounded-lg overflow-hidden border bg-black/5" data-testid="tour-step-video">
              {step.video.type === 'youtube' ? (
                <iframe
                  width="100%"
                  height="180"
                  src={`https://www.youtube.com/embed/${step.video.id}?rel=0&modestbranding=1`}
                  title={step.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  width="100%"
                  height="180"
                  controls
                  preload="metadata"
                  src={step.video.url}
                />
              )}
            </div>
          )}

          {!document.querySelector(step.target) && (
            <p className="text-xs text-amber-600 mb-3 italic">
              (This element isn't on the current page — navigate there to see it in action.)
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground" data-testid="tour-step-counter">
              Step {stepIndex + 1} of {steps.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={onSkip}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                data-testid="tour-skip-btn"
              >
                Skip
              </button>
              {!isFirst && (
                <button
                  onClick={onBack}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border hover:bg-muted"
                  data-testid="tour-back-btn"
                >
                  <ChevronLeft size={14} /> Back
                </button>
              )}
              <button
                onClick={onNext}
                className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90"
                data-testid="tour-next-btn"
              >
                {isLast ? 'Done' : 'Next'}
                {!isLast && <ChevronRight size={14} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TourProvider({ children }) {
  const [running, setRunning] = useState(false);
  const [currentTour, setCurrentTour] = useState(null);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);
  const { user, isAuthenticated, mustChangePassword } = useAuth();

  // Show the welcome tour once per USER (persisted on their account so it never
  // re-appears on every login or on another device). localStorage is only a cache.
  // Do NOT show it while the user is still being forced to change their password —
  // the tour must run only after the password change is complete (otherwise it
  // overlaps/blurs the mandatory password modal for new hires).
  useEffect(() => {
    if (!isAuthenticated || !user || mustChangePassword) {
      setShowWelcome(false);
      return;
    }
    const seenOnAccount = user.tour_welcome_seen === true;
    const seenLocal = localStorage.getItem('tour_welcome_seen') === 'true';
    const seen = seenOnAccount || seenLocal;
    if (seenOnAccount && !seenLocal) {
      localStorage.setItem('tour_welcome_seen', 'true');
    }
    setHasSeenWelcome(seen);
    setShowWelcome(!seen);
  }, [isAuthenticated, user, mustChangePassword]);

  const persistTourSeen = () => {
    localStorage.setItem('tour_welcome_seen', 'true');
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API}/auth/tour-seen`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };

  const dismissWelcome = () => {
    persistTourSeen();
    setShowWelcome(false);
    setHasSeenWelcome(true);
  };

  const resolveTourKey = (path) => {
    if (path.match(/\/ats\/applicants\/.+/)) return 'applicantDetail';
    for (const [p, key] of Object.entries(PATH_TO_TOUR)) {
      if (path.startsWith(p)) return key;
    }
    return null;
  };

  const startTourForPath = useCallback((path) => {
    const tourKey = resolveTourKey(path);
    if (tourKey && TOUR_STEPS[tourKey]) {
      setCurrentTour(tourKey);
      setSteps(TOUR_STEPS[tourKey]);
      setStepIndex(0);
      setRunning(true);
    } else {
      // Fallback: dashboard tour
      setCurrentTour('dashboard');
      setSteps(TOUR_STEPS.dashboard);
      setStepIndex(0);
      setRunning(true);
    }
  }, []);

  const startWelcomeTour = () => {
    persistTourSeen();
    setShowWelcome(false);
    setHasSeenWelcome(true);
    startTourForPath(window.location.pathname);
  };

  const startTour = useCallback((tourKey) => {
    if (TOUR_STEPS[tourKey]) {
      setCurrentTour(tourKey);
      setSteps(TOUR_STEPS[tourKey]);
      setStepIndex(0);
      setRunning(true);
    }
  }, []);

  const stopTour = useCallback(() => {
    setRunning(false);
    setCurrentTour(null);
    setStepIndex(0);
  }, []);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Done
      if (currentTour) {
        localStorage.setItem(`tour_completed_${currentTour}`, 'true');
      }
      stopTour();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const handleSkip = () => {
    stopTour();
  };

  const getAvailableTours = useCallback((path) => {
    const tours = [];
    for (const [p, key] of Object.entries(PATH_TO_TOUR)) {
      if (path.startsWith(p)) {
        tours.push({ key, label: key.charAt(0).toUpperCase() + key.slice(1), steps: TOUR_STEPS[key]?.length || 0 });
      }
    }
    if (path.match(/\/ats\/applicants\/.+/)) {
      tours.push({ key: 'applicantDetail', label: 'Applicant Detail', steps: TOUR_STEPS.applicantDetail?.length || 0 });
    }
    return tours;
  }, []);

  return (
    <TourContext.Provider value={{ running, startTour, startTourForPath, stopTour, showWelcome, dismissWelcome, startWelcomeTour, getAvailableTours, hasSeenWelcome }}>
      {children}
      {running && steps.length > 0 && (
        <TourOverlay
          steps={steps}
          stepIndex={stepIndex}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
          onClose={handleSkip}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour() {
  return useContext(TourContext);
}
